/**
 * Piecewise-linear HSL interpolation for risk scores (1–25).
 *
 * Hand-tuned breakpoints that match human color perception better
 * than mathematical power curves:
 *   1 → green (hue 120)
 *   3 → yellow-green (hue 75)
 *   6 → amber (hue 42)
 *  10 → orange (hue 18)
 *  15 → red (hue 6)
 *  25 → deep red (hue 0)
 */

interface Breakpoint {
  s: number; // score
  h: number; // hue
}

const BREAKPOINTS: Breakpoint[] = [
  { s: 1, h: 120 },
  { s: 3, h: 75 },
  { s: 6, h: 42 },
  { s: 10, h: 18 },
  { s: 15, h: 6 },
  { s: 25, h: 0 },
];

function interpolateHue(score: number): number {
  if (score <= BREAKPOINTS[0].s) return BREAKPOINTS[0].h;
  if (score >= BREAKPOINTS[BREAKPOINTS.length - 1].s)
    return BREAKPOINTS[BREAKPOINTS.length - 1].h;

  for (let i = 0; i < BREAKPOINTS.length - 1; i++) {
    const lo = BREAKPOINTS[i];
    const hi = BREAKPOINTS[i + 1];
    if (score >= lo.s && score <= hi.s) {
      const t = (score - lo.s) / (hi.s - lo.s);
      return lo.h + t * (hi.h - lo.h);
    }
  }
  return 0;
}

/**
 * Returns an HSL color string for a risk score (1–25).
 * Uses 75% saturation and 42% lightness for readable contrast.
 */
export function riskScoreColor(score: number): string {
  const hue = interpolateHue(score);
  return `hsl(${Math.round(hue)}, 75%, 42%)`;
}

/**
 * Returns a subtler version (15% opacity) for inline backgrounds.
 */
export function riskScoreBgColor(score: number): string {
  const hue = interpolateHue(score);
  return `hsla(${Math.round(hue)}, 75%, 42%, 0.15)`;
}

/**
 * Returns a vivid version for heatmap cells where the color gradient
 * must be clearly distinguishable on dark backgrounds.
 * Higher opacity (50%) and lifted lightness (50%) vs. the inline variant.
 */
export function riskScoreHeatmapBg(score: number): string {
  const hue = interpolateHue(score);
  return `hsla(${Math.round(hue)}, 75%, 50%, 0.5)`;
}
